import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Avatar, message } from 'antd';
import { isEmpty } from 'lodash';
import { RiNotification2Line } from 'react-icons/ri';
import { notificationPreview, notificationRemove } from 'appRedux/actions/Notifications';
import FollowUp from 'components/MessageNotification/modal/FollowUp';
import { useMessage } from 'util/messageLocalHook';

// ─── Types ────────────────────────────────────────────────────────────────────

type RefType = 'followup' | 'booking' | 'general' | 'system' | null;

type NotificationData = {
    notification_id: string | number;
    profile_pic?: string;
    notification_title?: string | null;
    notification_message?: string | null;
    notify_date?: string | null;
    ref_id: string | number | null;
    ref_type: RefType;
};

// ─── Type meta ────────────────────────────────────────────────────────────────

const notificationTypeMeta: Record<
    Exclude<RefType, null>,
    {
        avatarBackground: string;
        avatarColor: string;
        badgeBackground: string;
        badgeColor: string;
        label: string;
        action?: (
            refID: string | number | null,
            handlers: {
                onFollowUp: (id: string | number | null) => void;
                onNavigate: (path: string) => void;
            }
        ) => void;
    }
> = {
    followup: {
        avatarBackground: '#ede9fe',
        avatarColor: '#6d28d9',
        badgeBackground: '#f5f3ff',
        badgeColor: '#7c3aed',
        label: 'Follow-up',
        action: (refID, { onFollowUp }) => onFollowUp(refID),
    },
    booking: {
        avatarBackground: '#e0f2fe',
        avatarColor: '#0369a1',
        badgeBackground: '#eff6ff',
        badgeColor: '#2563eb',
        label: 'Booking',
        action: (refID, { onNavigate }) =>
            onNavigate(`/main/reservations/individual/form/${refID}`),
    },
    general: {
        avatarBackground: '#ecfccb',
        avatarColor: '#3f6212',
        badgeBackground: '#f7fee7',
        badgeColor: '#65a30d',
        label: 'General',
    },
    system: {
        avatarBackground: '#fee2e2',
        avatarColor: '#b91c1c',
        badgeBackground: '#fef2f2',
        badgeColor: '#dc2626',
        label: 'System',
    },
};

// ─── Inline Delete Confirm ────────────────────────────────────────────────────
// Renders as an absolute overlay inside the <li> row.
// It stays inside wrapperRef DOM tree — no portal, no Modal —
// so the outside-click handler never mistakes it for an outside click.

const DeleteConfirm: React.FC<{
    onConfirm: () => void;
    onCancel: () => void;
}> = ({ onConfirm, onCancel }) => {
    const { t } = useMessage()
    return (
        <div
            onClick={(e) => e.stopPropagation()} // prevent row click from firing
            style={{
                position: 'absolute',
                inset: 0,
                borderRadius: 10,
                background: 'rgba(255,255,255,0.96)',
                backdropFilter: 'blur(2px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                zIndex: 10,
                padding: '12px 16px',
                border: '1px solid #fca5a5',
            }}
        >
            <p style={{
                margin: 0,
                fontSize: 12.5,
                fontWeight: 600,
                color: '#1e293b',
                textAlign: 'center',
                lineHeight: 1.5,
            }}>
                {t('notification.delete_confirm')}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
                <button
                    onClick={onCancel}
                    style={{
                        height: 28,
                        padding: '0 14px',
                        borderRadius: 7,
                        border: '1px solid #e2e8f0',
                        background: '#f8fafc',
                        color: '#475569',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'background 0.12s',
                    }}
                >
                    {t('app.global.bt.cancel')}
                </button>
                <button
                    onClick={onConfirm}
                    style={{
                        height: 28,
                        padding: '0 14px',
                        borderRadius: 7,
                        border: 'none',
                        background: '#ef4444',
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'background 0.12s',
                    }}
                >
                    {t('app.global.bt.delete')}
                </button>
            </div>
        </div>
    )
};

// ─── Single notification row ──────────────────────────────────────────────────

const NotificationRow: React.FC<{
    item: NotificationData;
    onFollowUp: (id: string | number | null) => void;
    onNavigate: (path: string) => void;
    onDelete: (id: string | number) => void;
    /** Notify parent when this row's confirm overlay opens / closes */
    onConfirmOpen: (isOpen: boolean) => void;
    style?: React.CSSProperties;
}> = ({ item, onFollowUp, onNavigate, onDelete, onConfirmOpen, style }) => {
    const {
        notification_id, profile_pic, notification_title,
        notification_message, notify_date, ref_id, ref_type,
    } = item;

    const [removing, setRemoving] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [confirmVisible, setConfirmVisible] = useState(false);
    const typeMeta = ref_type ? notificationTypeMeta[ref_type] : undefined;

    const showConfirm = (e: React.MouseEvent) => {
        e.stopPropagation();
        setConfirmVisible(true);
        onConfirmOpen(true);
    };

    const handleConfirm = () => {
        setConfirmVisible(false);
        onConfirmOpen(false);
        setRemoving(true);
        setTimeout(() => onDelete(notification_id), 260);
    };

    const handleCancel = () => {
        setConfirmVisible(false);
        onConfirmOpen(false);
    };

    const handleRowClick = () => {
        if (confirmVisible) return;
        if (typeMeta?.action) {
            typeMeta.action(ref_id, { onFollowUp, onNavigate });
        }
    };

    return (
        <li
            onClick={handleRowClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '10px 14px',
                borderRadius: 10,
                marginBottom: 4,
                background: hovered ? '#f8faff' : '#fff',
                border: '1px solid',
                borderColor: confirmVisible ? '#fca5a5' : hovered ? '#dbe4ff' : '#f1f3f9',
                cursor: typeMeta?.action ? 'pointer' : 'default',
                position: 'relative',
                transition: 'all 0.18s ease',
                opacity: removing ? 0 : 1,
                transform: removing ? 'scale(0.97)' : 'scale(1)',
                maxHeight: removing ? 0 : 200,
                overflow: 'hidden',
                ...style,
            }}
        >
            {/* Avatar */}
            <Avatar
                size={36}
                src={profile_pic}
                alt={profile_pic}
                style={{
                    flexShrink: 0,
                    marginTop: 2,
                    background: typeMeta?.avatarBackground ?? '#e5e7eb',
                    color: typeMeta?.avatarColor ?? '#4b5563',
                    fontWeight: 700,
                    fontSize: 14,
                }}
            >
                {profile_pic || notification_title?.[0]?.toUpperCase()}
            </Avatar>

            {/* Body */}
            <div style={{ flex: 1, minWidth: 0 }}>
                {/* Type badge */}
                <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    padding: '2px 7px',
                    borderRadius: 20,
                    marginBottom: 4,
                    background: typeMeta?.badgeBackground ?? '#f3f4f6',
                    color: typeMeta?.badgeColor ?? '#6b7280',
                }}>
                    {typeMeta?.label ?? 'Notification'}
                </span>

                {/* Title */}
                <p style={{
                    margin: '0 0 2px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#1e293b',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 1.4,
                }}>
                    {notification_title}
                </p>

                {/* Message */}
                <p style={{
                    margin: '0 0 4px',
                    fontSize: 11.5,
                    color: '#64748b',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}>
                    {notification_message}
                </p>

                {/* Date */}
                <span style={{
                    fontSize: 11,
                    color: '#94a3b8',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                }}>
                    <span style={{
                        width: 5,
                        height: 5,
                        borderRadius: '50%',
                        background: '#cbd5e1',
                        display: 'inline-block',
                    }} />
                    {notify_date}
                </span>
            </div>

            {/* Delete button — visible on hover, hidden while confirm is showing */}
            <button
                onClick={showConfirm}
                title="Delete notification"
                style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    width: 24,
                    height: 24,
                    borderRadius: 7,
                    border: 'none',
                    background: '#fee2e2',
                    color: '#ef4444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    cursor: 'pointer',
                    opacity: hovered && !confirmVisible ? 1 : 0,
                    transform: hovered && !confirmVisible ? 'scale(1)' : 'scale(0.8)',
                    transition: 'opacity 0.15s ease, transform 0.15s ease',
                    flexShrink: 0,
                }}
            >
                <i className="icon icon-trash" />
            </button>

            {/* Inline confirm overlay — inside the row, inside wrapperRef */}
            {confirmVisible && (
                <DeleteConfirm onConfirm={handleConfirm} onCancel={handleCancel} />
            )}
        </li>
    );
};

// ─── Main NotificationBell ────────────────────────────────────────────────────

const NotificationBell: React.FC = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { t } = useMessage();

    const { notifications } = useSelector((state: any) => state);
    const { dataSource: notificationMessage, process } = notifications;

    const [open, setOpen] = useState(false);
    const [refID, setRefID] = useState<string | number | undefined | null>(undefined);
    const [isOpenFollow, setIsOpenFollow] = useState(false);
    // True while any row's DeleteConfirm overlay is visible
    const [confirmOpen, setConfirmOpen] = useState(false);

    const wrapperRef = useRef<HTMLDivElement>(null);

    // ─── Outside-click: close dropdown ────────────────────────────────────────
    // Skip when a confirm overlay is active (it lives inside wrapperRef, but
    // mousedown on its backdrop could still bubble out) or when the FollowUp
    // modal portal is open (renders outside wrapperRef in document.body).
    useEffect(() => {
        if (!open) return;

        const handler = (event: MouseEvent) => {
            if (isOpenFollow || confirmOpen) return;
            if (!wrapperRef.current) return;
            if (!wrapperRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handler, true);
        return () => document.removeEventListener('mousedown', handler, true);
    }, [open, isOpenFollow, confirmOpen]);

    // ─── Redux feedback ───────────────────────────────────────────────────────
    useEffect(() => {
        if (!isEmpty(process.action)) {
            if (process.status) {
                message.success(process.message);
                setTimeout(() => dispatch(notificationPreview()), 300);
            } else {
                message.error(process.message);
            }
        }
    }, [process, dispatch]);

    // Refresh list whenever count changes
    useEffect(() => {
        dispatch(notificationPreview());
    }, [dispatch, notificationMessage.length]);

    const count: number = notificationMessage?.length ?? 0;

    const handleFollowUp = useCallback((id: string | number | null) => {
        setRefID(id);
        setIsOpenFollow(true);
        setOpen(false);
    }, []);

    const handleNavigate = useCallback(
        (path: string) => {
            history.push(path);
            setOpen(false);
        },
        [history]
    );

    const handleDelete = useCallback(
        (id: string | number | null) => {
            dispatch(notificationRemove(id));
        },
        [dispatch]
    );

    return (
        <>
            {/* Bell trigger + dropdown */}
            <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-block' }}>

                {/* Bell button */}
                <button
                    onClick={() => setOpen((v) => !v)}
                    style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 34,
                        height: 34,
                        borderRadius: 9,
                        border: open ? '1px solid #dbe4ff' : '1px solid transparent',
                        background: open ? '#f0f4ff' : 'transparent',
                        cursor: 'pointer',
                        transition: 'background 0.15s, border-color 0.15s',
                        color: open ? '#4f46e5' : '#374151',
                    }}
                    title="Notifications"
                >
                    <RiNotification2Line className="text-xl" />

                    {count > 0 && (
                        <span style={{
                            position: 'absolute',
                            top: 5,
                            right: 5,
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: '#ef4444',
                            border: '1.5px solid #fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }} />
                    )}
                </button>

                {/* Dropdown panel */}
                {open && (
                    <div style={{
                        position: 'absolute',
                        top: 'calc(100% + 8px)',
                        right: 0,
                        width: 340,
                        background: '#fff',
                        borderRadius: 14,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.13), 0 1px 4px rgba(0,0,0,0.06)',
                        border: '1px solid #eef0f6',
                        zIndex: 1100,
                        overflow: 'hidden',
                        animation: 'noti-drop 0.2s cubic-bezier(.16,1,.3,1) forwards',
                    }}>

                        {/* Header */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '13px 16px 11px',
                            borderBottom: '1px solid #f1f3f9',
                            background: '#fafbff',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                                <div style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 9,
                                    background: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 15,
                                    boxShadow: '0 2px 8px rgba(99,102,241,0.25)',
                                }}>🔔</div>
                                <span style={{ fontWeight: 700, fontSize: 14.5, color: '#111827', letterSpacing: '-0.01em' }}>
                                    {t('notification.title')}
                                </span>
                                {count > 0 && (
                                    <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minWidth: 20,
                                        height: 20,
                                        padding: '0 6px',
                                        borderRadius: 20,
                                        background: '#6366f1',
                                        color: '#fff',
                                        fontSize: 11,
                                        fontWeight: 700,
                                    }}>{count}</span>
                                )}
                            </div>
                            <button
                                onClick={() => setOpen(false)}
                                style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 7,
                                    border: '1px solid #e5e7eb',
                                    background: '#fff',
                                    color: '#6b7280',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 13,
                                    cursor: 'pointer',
                                    transition: 'background 0.15s',
                                }}
                                title="Close"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Scrollable list */}
                        <div style={{
                            maxHeight: 340,
                            overflowY: 'auto',
                            padding: '8px 8px 4px',
                            scrollbarWidth: 'thin',
                            scrollbarColor: '#e2e8f0 transparent',
                        }}>
                            {count === 0 ? (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '32px 16px',
                                    gap: 8,
                                    color: '#94a3b8',
                                }}>
                                    <span style={{ fontSize: 30, opacity: 0.4 }}>🔕</span>
                                    <span style={{ fontSize: 13, fontWeight: 500 }}>{t('notification.empty')}</span>
                                </div>
                            ) : (
                                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                                    {notificationMessage.map((obj: NotificationData, idx: number) => (
                                        <NotificationRow
                                            key={obj.notification_id ?? idx}
                                            item={obj}
                                            onFollowUp={handleFollowUp}
                                            onNavigate={handleNavigate}
                                            onDelete={handleDelete}
                                            onConfirmOpen={setConfirmOpen}
                                            style={{ animationDelay: `${idx * 35}ms` }}
                                        />
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Footer */}
                        {count > 0 && (
                            <div style={{
                                padding: '8px 16px 12px',
                                borderTop: '1px solid #f1f3f9',
                                textAlign: 'center',
                                fontSize: 11.5,
                                color: '#94a3b8',
                            }}>
                                {count > 4
                                    ? t('notification.show_more', { count })
                                    : t('notification.total', { count })}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* FollowUp modal — outside dropdown to avoid z-index issues */}
            <FollowUp
                notikey="noti-topbar"
                isOpen={isOpenFollow}
                onChangeModal={setIsOpenFollow}
                reservationID={refID}
            />
        </>
    );
};

export default NotificationBell;